import PropTypes from 'prop-types';
import { Button, Divider, Form } from 'antd';
import styles from './index.module.scss';
import AntdInput from '../../../../../../../../../components/AntdInput';
import { DeleteOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import AccionaButton from '../../../../../../../../../components/AccionaButton';
import AntdFormItem from '../../../../../../../../../components/AntdFormItem';
import { useEffect } from 'react';

const CompressiveStrengthInput = ({ value, name = 'compressive_strength' }) => {
  const intl = useIntl();
  const form = Form.useFormInstance();

  useEffect(() => {
    const compressiveStrengthValue = form.getFieldValue(name);
    if (!compressiveStrengthValue || compressiveStrengthValue.length === 0) {
      form.setFieldsValue({
        [name]: [
          {
            day: undefined,
            value1: undefined,
            value2: undefined,
            value3: undefined,
          },
        ],
      });
    }
  }, [form, name, value]);

  return (
    <AntdFormItem
      label={intl.formatMessage({
        id: 'pages.quality.ocr.scannedDocuments.create.compressiveStrength',
      })}
      style={{ width: 'calc(100% - 60px)' }}
    >
      <Form.List
        name={name}
        rules={[
          {
            validator: (_, v) => {
              if (!Array.isArray(v) || v.length === 0) {
                return Promise.reject(
                  intl.formatMessage({
                    id: 'validations.required',
                  }),
                );
              }

              const nonEmptyItems = v.filter((item) => {
                return (
                  item?.day || item?.value1 || item?.value2 || item?.value3
                );
              });

              if (nonEmptyItems.length === 0) {
                return Promise.reject(
                  intl.formatMessage({
                    id: 'validations.at.least.one.valid',
                  }),
                );
              }
              const validItems = nonEmptyItems.filter((item) => {
                const hasValidDay =
                  item?.day &&
                  !isNaN(parseInt(item.day)) &&
                  parseInt(item.day) >= 1 &&
                  parseInt(item.day) <= 99;
                const hasAtLeastOneValue =
                  item?.value1 || item?.value2 || item?.value3;
                const isValid = hasValidDay && hasAtLeastOneValue;
                return isValid;
              });

              if (validItems.length === 0 && nonEmptyItems.length > 0) {
                return Promise.reject(
                  intl.formatMessage({
                    id: 'validations.at.least.one.valid',
                  }),
                );
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => {
          return (
            <div
              style={{ width: '100%' }}
              className={`${styles.container} ${
                errors.length > 0 ? styles.error : ''
              }`}
            >
              {fields.map((field, z) => (
                <div key={field.key} className={styles.block}>
                  <div className={styles.row}>
                    <AntdFormItem
                      {...field}
                      dependencies={fields.map((f, i) => [name, i, 'day'])}
                      name={[field.name, 'day']}
                      label="Día"
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'validations.required',
                          }),
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 99,
                          transform: (v) =>
                            v === '' ? undefined : Number(String(v).trim()),
                          message: intl.formatMessage(
                            {
                              id: 'validations.number.between',
                            },
                            { min: 1, max: 99 },
                          ),
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();

                            const allItems = form.getFieldValue(
                              'compressive_strength',
                            );
                            const duplicateCount = allItems.filter(
                              (item) => String(item?.day) === String(value),
                            ).length;

                            if (duplicateCount > 1) {
                              return Promise.reject(
                                intl.formatMessage({
                                  id: 'validations.duplicate.day',
                                }),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <AntdInput
                        onChange={() => {
                          form.validateFields([[name]]);
                        }}
                      />
                    </AntdFormItem>
                    <Button
                      icon={<DeleteOutlined />}
                      shape="circle"
                      type="primary"
                      onClick={() => {
                        remove(field.name);
                        setTimeout(() => {
                          const fields = form.getFieldValue(name);
                          form.validateFields([[name]]);
                          if (fields.length === 1) {
                            form.validateFields([[name, 0, 'day']]);
                          }
                        }, 500);
                      }}
                      className={styles.deleteBtn}
                    />
                  </div>

                  <div wrap className={styles.row}>
                    {[1, 2, 3].map((j) => (
                      <AntdFormItem
                        getValueFromEvent={(e) =>
                          e.target.value.replace(',', '.')
                        }
                        label={`Valor ${j}`}
                        className={styles.valueInput}
                        key={j}
                        dependencies={[
                          [name, z, 'day'],
                          ...[1, 2, 3].map((f) => {
                            return [name, z, `value${f}`];
                          }),
                        ]}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (value && String(value).trim() === '') {
                                return Promise.reject(
                                  intl.formatMessage({
                                    id: 'validations.no.whitespace.only',
                                  }),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                          {
                            validator: (_, value) => {
                              if (
                                value &&
                                (isNaN(Number(value)) || Number(value) < 0)
                              ) {
                                return Promise.reject(
                                  intl.formatMessage({
                                    id: 'validations.format.float',
                                  }),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                          {
                            validator: (_, value) => {
                              const formValues = form.getFieldsValue();
                              const currentItem =
                                formValues[name]?.[field.name];

                              if (
                                currentItem?.day &&
                                !isNaN(parseInt(currentItem.day)) &&
                                parseInt(currentItem.day) >= 1 &&
                                parseInt(currentItem.day) <= 99
                              ) {
                                const hasValue1 =
                                  currentItem?.value1 &&
                                  !isNaN(Number(currentItem.value1)) &&
                                  Number(currentItem.value1) >= 0;
                                const hasValue2 =
                                  currentItem?.value2 &&
                                  !isNaN(Number(currentItem.value2)) &&
                                  Number(currentItem.value2) >= 0;
                                const hasValue3 =
                                  currentItem?.value3 &&
                                  !isNaN(Number(currentItem.value3)) &&
                                  Number(currentItem.value3) >= 0;

                                const hasAtLeastOneValue =
                                  hasValue1 || hasValue2 || hasValue3;

                                if (!hasAtLeastOneValue) {
                                  return Promise.reject(
                                    intl.formatMessage({
                                      id: 'validations.at.least.one.value.required',
                                    }),
                                  );
                                }
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                        name={[field.name, `value${j}`]}
                      >
                        <AntdInput
                          onChange={() => {
                            form.validateFields([[name]]);
                          }}
                        />
                      </AntdFormItem>
                    ))}
                  </div>

                  <Divider style={{ margin: '12px 0' }} />
                </div>
              ))}

              <AccionaButton
                type="primary"
                size="small"
                onClick={() => {
                  if (errors.length === 0) {
                    add({
                      day: undefined,
                      value1: undefined,
                      value2: undefined,
                      value3: undefined,
                    });
                    const fields = form.getFieldValue(name);
                    setTimeout(() => {
                      form.validateFields([[name, fields.length - 1, 'day']]);
                    }, 1000);
                  }
                }}
                style={{
                  margin: 0,
                  height: 40,
                  width: '100%',
                  padding: '7.5px 15px',
                }}
                icon={<i className="fas fa-plus" />}
                msg={intl.formatMessage({ id: 'add' })}
                classIcon="fas fa-plus"
                disabled={errors.length > 0}
              />
              <Form.ErrorList errors={errors} />
            </div>
          );
        }}
      </Form.List>
    </AntdFormItem>
  );
};

CompressiveStrengthInput.propTypes = {
  value: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value1: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value2: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value3: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  onChange: PropTypes.func,
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  disabled: PropTypes.bool,
  name: PropTypes.string,
};

CompressiveStrengthInput.defaultProps = {
  disabled: false,
  name: 'compressive_strength',
  onChange: () => {},
  onAdd: () => {},
  onDelete: () => {},
};

export default CompressiveStrengthInput;
